import { IncomingHttpHeaders, IncomingMessage, ServerResponse } from 'http';
import { defineMessage } from 'react-intl';
import type { GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { formatLocale } from '@/src/listings/i18n/locale';
import { setCacheControlForErrorResponse } from '@/src/listings/utils/cacheControl';

export type EventListingError = {
  statusCode?: number;
  error?: string | undefined;
};

export type ErrorResponse = {
  statusCode: number;
  message: string;
  description: string;
};

export const errorMessages = {
  genericError: {
    message: defineMessage({ id: 'Something went wrong', defaultMessage: 'Something went wrong' }),
    description: defineMessage({
      id: "We can't find what you looked for, but we have other events you might like.",
      defaultMessage: "We can't find what you looked for, but we have other events you might like.",
    }),
  },
};

const CLIENT_ERROR_MAP: Record<number, ErrorResponse> = {
  [400]: {
    statusCode: 400,
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
  [403]: {
    statusCode: 502,
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
  [404]: {
    statusCode: 404,
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
  [500]: {
    statusCode: 500,
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
  [502]: {
    statusCode: 502,
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
  [503]: {
    statusCode: 503,
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
};

export const handle404Error = (
  res: ServerResponse<IncomingMessage>,
  locale: string,
  messages: Record<string, string>,
  globalHeaderProps?: GlobalHeaderProps,
  requestHeaders?: IncomingHttpHeaders,
) => {
  res.statusCode = 404;
  setCacheControlForErrorResponse(res, requestHeaders);
  return {
    props: {
      error: {
        statusCode: 404,
        message: errorMessages.genericError.message.id,
        description: errorMessages.genericError.description.id,
      },
      context: null,
      canonicalUrl: null,
      query: {},
      locale: formatLocale(locale),
      messages,
      ...(globalHeaderProps !== undefined && { globalHeaderProps }),
    },
  };
};

const getCustomErrorResponse = (
  res: ServerResponse<IncomingMessage>,
  statusCode: number,
  locale: string,
  messages: Record<string, string>,
  globalHeaderProps?: GlobalHeaderProps,
) => {
  const clientError = CLIENT_ERROR_MAP[statusCode] || CLIENT_ERROR_MAP[500];
  res.statusCode = clientError.statusCode;
  return {
    props: {
      error: clientError,
      context: null,
      canonicalUrl: null,
      query: {},
      locale: formatLocale(locale),
      messages,
      ...(globalHeaderProps !== undefined && { globalHeaderProps }),
    },
  };
};

export const handleError = (
  res: ServerResponse<IncomingMessage>,
  error: EventListingError,
  locale: string,
  messages: Record<string, string>,
  globalHeaderProps?: GlobalHeaderProps,
) => {
  const statusCode = error.statusCode || 400;
  res.statusCode = statusCode === 503 ? 503 : statusCode >= 500 ? 500 : statusCode;
  console.error(`Error ${error.statusCode}: ${error.error || 'Unknown error'}`);
  return getCustomErrorResponse(res, res.statusCode, locale, messages, globalHeaderProps);
};
